import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../../../layouts/Layout";
import BetList from "./bet-list/BetList";
import GamingRevenueGraphAndStats from "./graph-and-stats/GamingRevenueGraphAndStats";
import SearchAndTotalSummary from "./search-total-summary/SearchAndTotalSummary";
import { alertPlatform, getSearchOptions } from "../../../services/api";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { pagecount } from "../../../utils/reuse";
import { useToast } from "../../../context/ToastContext";
import { RAW_FILTER_DATA } from "../../../shared/common";
import { storeAlertPlatFormFilters } from "../../../store/filter.slice";
import { filterOptionsActions } from "../../../store/FiltersOptions";

const Dashboard = () => {
  const sessionData = useSelector((state) => state.auth.sessionData);
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const groupLoadedRef = useRef(false);
  const channelLoadedRef = useRef(false);
  const headers = useMemo(() => {
    return { sessionid: sessionData.sessionId };
  }, [sessionData.sessionId]);
  const [loadingState, setLoadingState] = useState({
    searchSummary: false,
    gamingStats: false,
    betList: false
  });
  
  const [defaultDate] = useState(() => {
    const nowUTC = new Date(new Date().toUTCString());
    const hourUTC = nowUTC.getUTCHours();

    const offsetDays = hourUTC >= 21 ? 1 : 2;

    const maxAllowedDate = new Date();
    maxAllowedDate.setUTCDate(nowUTC.getUTCDate() - offsetDays);

    return [maxAllowedDate, maxAllowedDate];
  });
  const alertPlatformState = useSelector(state => state.alertPlatformFilter.bettingDataFilters);
  const storedFilterOptions = useSelector((state) => state.filterOptions);
  const [searchRequest, setSearchRequest] = useState(alertPlatformState);
  const [betAndAlertData, setBetAndAlertData] = useState({
    events: {},
    sports: {},
    tournaments: {},
  });
  const [betList, setBetList] = useState([]);
  const [searchOptions, setSearchOptions] = useState({
    group: [],
    location: [],
    channel: []
  });
  const [defaultGroup, setDefaultGroup] = useState('');
  const [defaultState, setDefaultState] = useState('');

  const [requestType, setRequestType] = useState('');
  const [page, setPage] = useState(1);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(pagecount + 1);
  const [last, setLast] = useState(true);
  const [paging, setPaging] = useState(false);
  const [sortField, setSortField] = useState('reportdate');
  const [sortOrder, setSortOrder] = useState(-1);
  const [playerID, setPlayerID] = useState(alertPlatformState?.playerID ?? '');


  // Get Bet Report data 
  const getBetReport = useCallback((data) => {
    setLoadingState(prev => ({ ...prev, searchSummary: true, gamingStats: true }));
    if(data.betTypeIDs){
      delete data.betTypeIDs;
    }
    alertPlatform('betreport', {}, data, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const betData = response.data;
        betData.events = betData?.byEvent ? betData?.byEvent : {}
        betData.sports = betData?.bySport ? betData.bySport:  {}
        betData.tournaments = betData?.byTournament ? betData.byTournament: {}
        setBetAndAlertData(betData);
        if(!betData?.byEvent?.betSummary?.length){
           showToast("warn", "No records found for selected filters");
        }
      } else {
        setBetAndAlertData({
          events: {},
          sports: {},
          tournaments: {},
        });
        const error = response.error;
        showToast(error.severity, error?.errorMsg || error?.summary);
      }
      setLoadingState(prev => ({ ...prev, searchSummary: false, gamingStats: false }));
    });
  }, [dispatch, headers, showToast]);

  // Get Bet List data
  const getBetList = useCallback((data) => {
    setLoadingState(prev => ({ ...prev, betList: true }));
    const params = {
      offset: offset,
      limit: limit,
      sort_by: sortField,
      sort_order: sortOrder === -1 ? 'desc' :'asc'
    };
    if (requestType) {
      params.groupBy = requestType;
    }
    if(playerID){
      data.playerID = playerID;
    }
    alertPlatform('betlist', params, data, headers, dispatch, (response) => {
      if (response.result === "SUCCESS") {
        const responseData = response.data;
        const result = responseData?.length > 0 ? responseData.slice(0, pagecount) : [];
        if (result.length > 0) {
          setLast(responseData.length <= pagecount);
          setPaging(true);
          setBetList(result);
        } else {
          setLast(true);
          setPaging(false);
          setBetList([]);
        }
      } else {
        setLast(true);
        setPaging(false);
        setBetList([]);
      }
      setLoadingState(prev => ({ ...prev, betList: false }));
    });
  }, [dispatch, headers, limit, offset, requestType, sortField, sortOrder, playerID]);

  // Search Filter
  const searchFilterHandler = useCallback((filters) => {
    setPage(1); // Reset to page 1 only on filters
    setOffset(0);
    setSearchRequest(filters);
  }, []);

  useEffect(() => {
    searchFilterHandler(searchRequest);
  }, [searchFilterHandler, searchRequest]);

  const buildPayload = useCallback((request, api = '') => {
    const data = {};

    data.groupID = request.groupID ? request.groupID : defaultGroup?.id;
    data.stateID = request.stateID ? request.stateID : defaultState?.id;
    if (request.events?.length) data.events = request.events.map((rt) => rt.id);
    if (request.sports?.length) data.sportIDs = request.sports.map((s) => s.id);
    if (request.tournaments?.length) data.tournamentIDs = request.tournaments.map((r) => r.id);
    if (request.players?.length) data.players = request.players.map((r) => r.id);
    if (request.locations?.length) data.propertyIDs = request.locations.map(r => r.id);
    const channelIDs = request.channel?.length ? request.channel.map(r => r.id) : searchOptions?.channel?.map(c => c.id);
    if (api) {
      data.channelIDs = channelIDs;
    } else if (!request.locations?.length) {
      data.channelIDs = channelIDs
    }
    if (request.betNames?.length) data.betTypeIDs = request.betNames.map((r) => r.id);

    
    const startDate = request?.date?.date?.[0]
      ? moment(request.date.date[0]).format("YYYY-MM-DD")
      : moment(defaultDate[0]).format("YYYY-MM-DD");
    const endDate = request?.date?.date?.[1]
      ? moment(request.date.date[1]).format("YYYY-MM-DD")
      : startDate;

    data.startDate = startDate;
    data.endDate = endDate;

    return data;
  }, [defaultDate, defaultGroup?.id, defaultState?.id, searchOptions?.channel]);


  useEffect(() => {
    if (defaultGroup?.id &&  defaultState?.id) {
      const data = buildPayload(searchRequest, 'betReport');
      getBetReport(data);
    }
  }, [searchRequest, buildPayload, getBetReport, defaultGroup?.id, defaultState?.id]);

  useEffect(() => {
    if (defaultGroup?.id &&  defaultState?.id) {
      const data = buildPayload(searchRequest);
      getBetList(data);
    }
  }, [searchRequest, buildPayload, getBetList, defaultGroup?.id, defaultState?.id]);

  const handlePageChange = (currentPage) => {
    setPaging(false);
    setPage(currentPage);
    const offsetVal = (currentPage - 1) * pagecount;
    setOffset(offsetVal);
    setLoadingState(prev => ({ ...prev, betList: true }));
  };
  const sortHandler = ({sortField, sortOrder})=> {
    setSortField(sortField);
    setSortOrder(sortOrder);
    handlePageChange(1);
  }

  const getSearchOptionsHandler = useCallback((type, groupParam = '') => {
    const getResponse = (type) => (response) => {
      if (response.result === "SUCCESS") {
        let filterOptions = response.data?.length ? response.data.map(r => ({ id: r.id, name: r.name })).filter(r => r.name?.toLowerCase() !=='unknown') : [];
        if(type === 'channel'){
          const channelIds = filterOptions.map(s => s.id);
          filterOptions = RAW_FILTER_DATA.newchannels.map(f => { return channelIds.includes(f.id) ? f : RAW_FILTER_DATA.newchannels.find(c => c.id === f.id)})
           dispatch(filterOptionsActions.storeFilterOptions({ key: type, options: filterOptions }))
        }
        if(type === 'group' && filterOptions?.length){
          setDefaultGroup(filterOptions[0])
          dispatch(filterOptionsActions.storeFilterOptions({ key: type, options: filterOptions }))
        }
        if(type === 'location' && filterOptions?.length){
          setDefaultState(filterOptions[0])
        }
        setSearchOptions((prev) => ({
          ...prev,
          [type]: [...filterOptions]
        }));
      } else {
         setSearchOptions((prev) => ({
          ...prev,
          [type]: []
        }));
      }
    };
    getSearchOptions(type, headers, dispatch, getResponse(type), groupParam);

  }, [dispatch, headers]);

  useEffect(() => {
    if (defaultGroup?.id) {
      getSearchOptionsHandler('location', `groups[]=${defaultGroup?.id}`);
    } 
  }, [getSearchOptionsHandler, defaultGroup]);
  useEffect(() => {
    if (groupLoadedRef.current) return;
    
    if (storedFilterOptions['group']?.length === 0) {
      getSearchOptionsHandler('group');
    } else {
      setDefaultGroup(storedFilterOptions['group'][0])
      setSearchOptions((prev) => ({
        ...prev,
        'group': [...storedFilterOptions['group']]
      }));
    }
    groupLoadedRef.current = true;
  }, [getSearchOptionsHandler, storedFilterOptions]);
  useEffect(() => {
    if (channelLoadedRef.current) return;
    
    if (storedFilterOptions['channel']?.length === 0) {
      getSearchOptionsHandler('channel');
    } else {
      setSearchOptions((prev) => ({
        ...prev,
        'channel': [...storedFilterOptions['channel']]
      }));
    }
    channelLoadedRef.current = true;
  }, [getSearchOptionsHandler, storedFilterOptions]);

  const searchRequestHandler = (data) => {
    setSearchRequest(data);
  };
  const filterHandler = (value, type) => {
    const data = searchOptions[type]?.length ? searchOptions[type] : [];
    const find = data.find(d => d.id === value);
    if (type === 'group') {
      setDefaultGroup(find)
      setDefaultState({})
      setBetAndAlertData({
        events: {},
        sports: {},
        tournaments: {},
      })
      setBetList([])
    }
  }
  const onTabChangeHandler = (type) => {
    if (type && alertPlatformState?.betNames?.length) {
      let betNames = [];
      if (type === 'single') {
        betNames = alertPlatformState?.betNames.filter(b => b.name.toLowerCase() === 'single')
      } else if (type === 'parlay') {
        const options = ['combo', 'parlay', 'progressive parlay'];
        betNames = alertPlatformState?.betNames.filter(b => options.includes(b.name.toLowerCase()))
      }
      const filters = { ...alertPlatformState, betNames };
      setSearchRequest(filters);
      dispatch(storeAlertPlatFormFilters(filters));
    }
    setRequestType(type);
    setPage(1);
    setOffset(0);
    setLoadingState(prev => ({ ...prev, betList: true }));
  }
  const playerSearchHandler = (searchData) => {
    setPlayerID(searchData?.playerID ?? '');
    setPage(1);
    setOffset(0);
  }
  return (
    <section className="analyst-home-section">
      <Layout>
        <SearchAndTotalSummary searchRequestHandler={searchRequestHandler} filterHandler={filterHandler} totalData={betAndAlertData} searchOptions={searchOptions} loading={loadingState.searchSummary} alertPlatformState={alertPlatformState} defaultDate={defaultDate} defaultGroup={defaultGroup} defaultState={defaultState} requestType={requestType} screenType={'dashboard'}/>
        <GamingRevenueGraphAndStats betData={betAndAlertData} loading={loadingState.gamingStats} groupId={defaultGroup?.id}/>
        <BetList betList={betList} loading={loadingState.betList} page={page} paging={paging} last={last} sortField={sortField} sortOrder={sortOrder} handlePageChange={handlePageChange} sortHandler={sortHandler} onTabChange={onTabChangeHandler} playerSearchHandler={playerSearchHandler} groupId={defaultGroup?.id}/>
      </Layout>
    </section>
  );
};

export default Dashboard;
